import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CATALOGS, RESOURCES } from "../../../constants/route-paths";
import {
  addLabor,
  editLabor as editLaborAPI,
  getLabor,
} from "../../../actions/inventory.action";
import Button from 'react-bootstrap/Button';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import _ from 'lodash';
import TextAreaInput from "../../../components/inputs/textarea-input";
import { AmountInput } from "../../../components/inputs/new-input";
import { ValidateField, ValidatorTypes } from "../../../util/validators";
import { useMatch, useNavigate, useParams } from "react-router";
import ResourceTemplateSummaryModal from "./resource-template-summary-modal";
import ResourceCostPropagationDialog from "./resource-cost-propagation-dialog";
import { clearResourcesAction } from "../../../slice/resources";

/**
 * Component for adding or editing a Labor resource in the catalog.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} props.writePermission - RBAC write permission flag.
 * @returns {JSX.Element} The rendered AddLabor form component.
 */
export default function AddLabor({ writePermission }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { laborId } = useParams();
  const match = useMatch(`/catalogs/resources/edit/${laborId}`);
  const resources = useSelector(state => state.resources.list) || [];

  const [fields, setFields] = useState({
    skill: {
      name: "skill",
      label: "Resource",
      type: "textarea",
      touched: false,
      error: false,
      valid: true,
      value: "",
      validation: [ValidatorTypes.REQUIRED, ValidatorTypes.TEXTAREA],
    },
    cost: {
      name: "cost",
      label: "Average Cost / Unit",
      type: "number",
      touched: false,
      error: false,
      valid: true,
      value: "",
      validation: [ValidatorTypes.REQUIRED],
    },
    isPersonnel: {
      name: "isPersonnel",
      label: "Personnel Resource",
      type: "checkbox",
      touched: false,
      error: false,
      valid: true,
      value: true,
      validation: [],
    },
  })
  const [state, setState] = useState({
    levels: [],
    hourlyCost: [],
    levelsError: "",
    initialCost: null,
  })
  const [propagationDialog, setPropagationDialog] = useState({
    open: false,
    pendingData: null,
  })
  const [summaryModal, setSummaryModal] = useState({
    open: false,
    summary: null,
  })

  useEffect(() => {
    if (match && laborId) {
      const existingLabor = resources.find(item => item.id === laborId);
      if (existingLabor) {
        editLabor(existingLabor);
      } else {
        dispatch(getLabor(laborId)).then((labor) => {
          editLabor(labor);
        }).catch(e => console.error(e));
      }
    }
  }, [laborId])

  const editLabor = (item) => {
    setState(prev => ({
      ...prev,
      edit: true,
      itemId: item && item.id,
      initialCost: item?.cost !== undefined && item?.cost !== null ? parseFloat(item.cost) : null,
    }));
    setFields(prev => ({
      ...prev,
      skill: { ...prev.skill, value: item?.skill || '' },
      cost: { ...prev.cost, value: item?.cost || '' },
      isPersonnel: { ...prev.isPersonnel, value: item?.is_personnel !== undefined ? item.is_personnel : true },
    }));
  };

  const inputChangeHandler = (e) => {
    const { error, valid } = ValidateField({ value: e.target.value, validation: fields[e.target.name].validation });
    setFields(prev => ({
      ...prev,
      [e.target.name]: {
        ...prev[e.target.name],
        value: e.target.value,
        touched: true,
        error: error,
        valid: valid
      },
    }));
  };

  const cancelHandler = () => {
    navigate(`/${CATALOGS}/${RESOURCES}`);
  };

  const executeEditLabor = (data, propagateOptions) => {
    const payload = propagateOptions ? { ...data, propagateOptions } : data;
    dispatch(editLaborAPI(payload, state.itemId, { updateLocally: true })).then((res) => {
      const updatedTemplates = _.get(res, 'updatedTemplates', null);
      const itemTemplates = _.get(updatedTemplates, 'itemTemplates', []);
      const taskTemplates = _.get(updatedTemplates, 'taskTemplates', []);
      const skippedItemTemplates = _.get(updatedTemplates, 'skippedItemTemplates', []);
      const skippedTaskTemplates = _.get(updatedTemplates, 'skippedTaskTemplates', []);
      const itemTemplatesCount = _.get(updatedTemplates, 'itemTemplatesCount', itemTemplates.length);
      const taskTemplatesCount = _.get(updatedTemplates, 'taskTemplatesCount', taskTemplates.length);
      const skippedItemTemplatesCount = _.get(updatedTemplates, 'skippedItemTemplatesCount', skippedItemTemplates.length);
      const skippedTaskTemplatesCount = _.get(updatedTemplates, 'skippedTaskTemplatesCount', skippedTaskTemplates.length);

      const hasItemsToShow = (itemTemplatesCount + taskTemplatesCount + skippedItemTemplatesCount + skippedTaskTemplatesCount) > 0;
      if (hasItemsToShow && propagateOptions) {
        setSummaryModal({
          open: true,
          summary: {
            ...updatedTemplates,
            resourceName: data.skill || fields.skill.value,
          },
        });
      } else {
        cancelHandler();
      }
    }).catch(e => console.error(e));
  };

  const handlePropagationConfirm = (options) => {
    const pendingData = propagationDialog.pendingData;
    setPropagationDialog({ open: false, pendingData: null });
    if (pendingData) {
      executeEditLabor(pendingData, options);
    }
  };

  const submitForm = () => {
    const { itemId } = state;
    let _fields = _.cloneDeep(fields)
    let isFormValid = true;
    for (let key in _fields) {
      const { error, valid } = ValidateField({ value: _fields[key].value, validation: _fields[key].validation });
      _fields[key].touched = true;
      _fields[key].valid = valid;
      _fields[key].error = error;
      isFormValid = isFormValid && valid;
    }
    if (!isFormValid) {
      setFields(_fields)
    } else {
      const data = {
        skill: _fields.skill.value,
        cost: _fields.cost.value,
        isPersonnel: _fields.isPersonnel.value,
        status: 'ACTIVE',
      };
      if (!itemId) {
        dispatch(addLabor(data)).then(() => {
          dispatch(clearResourcesAction());
          cancelHandler();
        }).catch(e => console.error(e));
      } else {
        setPropagationDialog({
          open: true,
          pendingData: data,
        });
      }
    }
  };

  return (
    <div className="purchase-order-block">

      <h2 className="content-heading text-uppercase">{state.edit ? "Edit Resource" : "Add a Resource"}</h2>
      <div className="col-12 breadcrumb-block p-0">
        <Breadcrumb>
          <Breadcrumb.Item onClick={cancelHandler}>Resources</Breadcrumb.Item>
          <Breadcrumb.Item active>{state.edit ? "Edit Resource" : "Add a Resource"}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className="row">

        <div className="col-lg-12">
          <div className="mt-3">
            <TextAreaInput
              {...fields.skill}
              data-cy="input-skill"
              onChange={(e) => inputChangeHandler(e)}
            />
          </div>
        </div>
        <div className="col-lg-12">
          <div className="mt-3">
            <AmountInput
              {...fields.cost}
              data-cy="input-cost"
              onChange={(e) => {
                if (e.target.value >= 0)
                  inputChangeHandler(e)
              }}
            />
          </div>
        </div>
        <div className="col-lg-12">
          <div className="mt-3">
            <div className="form-check form-switch d-flex align-items-center gap-2">
              <input
                className="form-check-input switch-checkbox mb-0"
                type="checkbox"
                role="switch"
                id="isPersonnelToggle"
                name="isPersonnel"
                checked={fields.isPersonnel.value}
                onChange={(e) => {
                  setFields(prev => ({
                    ...prev,
                    isPersonnel: {
                      ...prev.isPersonnel,
                      value: e.target.checked
                    }
                  }))
                }}
              />
              <label className="form-check-label mb-0" htmlFor="isPersonnelToggle">Personnel Resource <small className="text-muted ms-2">(Included in Planned Hours calculations)</small></label>
            </div>
          </div>
        </div>
        <div className="col-12 d-flex justify-content-end gap-3 mt-3">
          {writePermission && (<Button id="submit-labor" onClick={submitForm} className="secondarybtn">DONE</Button>)}
          <Button id="cancel-labor" onClick={cancelHandler} className="secondarybtn cancelbtn">CANCEL</Button>
        </div>
      </div>
      <ResourceCostPropagationDialog
        open={propagationDialog.open}
        onClose={() => setPropagationDialog({ open: false, pendingData: null })}
        onConfirm={handlePropagationConfirm}
      />
      <ResourceTemplateSummaryModal
        open={summaryModal.open}
        onClose={cancelHandler}
        summary={summaryModal.summary}
      />
    </div>
  );

}
